/**
 * 前端通用工具函数集合。
 * 这里集中放 Markdown 渲染、复制、时间格式化和角色样式映射，避免页面里重复实现。
 */
import MarkdownIt from "markdown-it";
import type { Options as MarkdownItOptions } from "markdown-it";
import hljs from "highlight.js/lib/core";
import bash from "highlight.js/lib/languages/bash";
import css from "highlight.js/lib/languages/css";
import javascript from "highlight.js/lib/languages/javascript";
import json from "highlight.js/lib/languages/json";
import markdown from "highlight.js/lib/languages/markdown";
import python from "highlight.js/lib/languages/python";
import typescript from "highlight.js/lib/languages/typescript";
import xml from "highlight.js/lib/languages/xml";
import { ChatRole, ROLE_DISPLAY_TEXT, ROLE_CLASS_MAP } from "~/enums";

/**
 * 格式化时间戳为 HH:mm:ss 形式
 * @param timestamp
 */
export const formatTime = (timestamp: number) => {
  return new Date(timestamp).toLocaleTimeString("zh-CN");
};

/**
 * 格式化时间戳为完整日期时间形式（用于分享页面等需要完整时间的场景）
 * @param timestamp - 时间戳（毫秒）
 * @returns 格式化的日期时间字符串，如 "2024/1/15 15:38:53"
 */
export const formatDateTime = (timestamp: number) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  return date.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

/**
 * 格式化聊天消息时间：今天显示“今天 HH:mm”，其它日期显示“YYYY/MM/DD HH:mm”。
 */
export const formatChatMessageTime = (value?: number | string | Date | null) => {
  if (!value) return "";

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  const time = date.toLocaleTimeString("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  if (isToday) {
    return `今天 ${time}`;
  }

  const dateText = date.toLocaleDateString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  return `${dateText} ${time}`;
};

interface ChatMessageWithTime {
  createdAt?: number | string | Date;
  timestamp?: number | string | Date;
}

/**
 * SDK 的临时消息不带时间字段，首次进入消息列表时补齐并保留该时间。
 */
export const ensureChatMessageTimes = <T extends ChatMessageWithTime>(
  messages: T[],
  now: number = Date.now(),
) => {
  messages.forEach((message) => {
    if (!message.createdAt && !message.timestamp) {
      message.createdAt = now;
    }
  });
};

export const dateId = () => {
  const now = new Date();

  return `${now.getTime()}_${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * 格式化 JSON 字符串用于显示
 */
export const formatJson = (obj: any) => {
  return JSON.stringify(obj, null, 2);
};

/**
 * 复制文本到剪贴板
 */
export const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    ElMessage.success("成功复制到剪贴板！");
  } catch {
    ElMessage.error("复制失败");
  }
};

/**
 * 复制文本
 * 事件委托处理代码块复制（v-html 内容无法直接绑定 Vue 事件）
 * md中的复制代码
 * @param e
 */
export const handleCopyClick = async (e: MouseEvent) => {
  const target = e.target as HTMLElement;
  const btn = target.closest<HTMLButtonElement>(".code-copy-btn");
  if (!btn) return;

  e.preventDefault();
  e.stopPropagation();

  const codeBlock = btn.closest(".code-block-wrapper")?.querySelector("code");
  if (!codeBlock) return;

  try {
    await navigator.clipboard.writeText(codeBlock.textContent || "");
    const originalHTML = btn.innerHTML;
    // 显示复制成功图标，2秒后恢复原状
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>';
    setTimeout(() => {
      btn.innerHTML = originalHTML;
    }, 2000);
    ElMessage.success("复制成功");
  } catch {
    ElMessage.error("复制失败");
  }
};

// 初始化 markdown-it 实例，集成代码高亮
let md: InstanceType<typeof MarkdownIt> | null = null;

const registerHighlightLanguages = () => {
  hljs.registerLanguage("bash", bash);
  hljs.registerLanguage("shell", bash);
  hljs.registerLanguage("sh", bash);
  hljs.registerLanguage("css", css);
  hljs.registerLanguage("javascript", javascript);
  hljs.registerLanguage("js", javascript);
  hljs.registerLanguage("json", json);
  hljs.registerLanguage("markdown", markdown);
  hljs.registerLanguage("md", markdown);
  hljs.registerLanguage("python", python);
  hljs.registerLanguage("py", python);
  hljs.registerLanguage("typescript", typescript);
  hljs.registerLanguage("ts", typescript);
  hljs.registerLanguage("html", xml);
  hljs.registerLanguage("xml", xml);
};

const codeCopyIcon =
  '<svg viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>';

const renderCodeBlock = (renderer: InstanceType<typeof MarkdownIt>, code: string, lang = "") => {
  const escapedLang = renderer.utils.escapeHtml(lang);
  const langText = escapedLang ? escapedLang.toUpperCase() : "CODE";
  const codeClass = escapedLang ? ` class="hljs ${escapedLang}"` : "";

  return `<div class="code-block-wrapper" data-lang="${escapedLang}"><div class="code-header"><span class="code-lang">${langText}</span><button type="button" class="code-copy-btn" title="复制代码" aria-label="复制代码">${codeCopyIcon}</button></div><pre><code${codeClass}>${code}</code></pre></div>`;
};

const getMarkdownRenderer = () => {
  if (md) return md;

  registerHighlightLanguages();

  const renderer = new MarkdownIt({
  html: false, // ⚠️ 安全起见默认关闭，AI 通常只输出 Markdown
  linkify: true, // 自动识别链接
  typographer: true, // 启用智能引号和替换
  breaks: true, // 转换换行符为 <br>
  });

  renderer.renderer.rules.fence = (tokens, idx) => {
    const token = tokens[idx];
    const info = token.info ? token.info.trim() : "";
    const lang = info ? info.split(/\s+/)[0] : "";

    // 代码高亮
    if (lang && hljs.getLanguage(lang)) {
      try {
        const highlighted = hljs.highlight(token.content, { language: lang }).value;
        // 添加语言标识和复制按钮（使用事件委托方式，不需要onclick）
        return renderCodeBlock(renderer, highlighted, lang);
      } catch (__) {}
    }

    // 如果没有指定语言或高亮失败，转义后返回
    return renderCodeBlock(renderer, renderer.utils.escapeHtml(token.content), lang);
  };

  md = renderer;
  return renderer;
};

/**
 * 渲染 Markdown 内容为 HTML
 */
export const renderMarkdown = (content: string): string => {
  if (!content) return "";
  try {
    return getMarkdownRenderer().render(content);
  } catch (error) {
    console.error("Markdown 渲染失败:", error);
    return content; // 降级处理：返回原始内容
  }
};

// 开发环境检测
export const isDev = computed(() => {
  return import.meta.env.DEV;
});

// 生产环境检测
export const isProd = computed(() => {
  return import.meta.env.PROD;
});

/**
 * 获取角色显示文本
 */
export const getRoleDisplayText = (role: string): string => {
  return ROLE_DISPLAY_TEXT[role as ChatRole] || role;
};

/**
 * 获取角色样式类名
 */
export const getRoleClass = (role: string): string => {
  return ROLE_CLASS_MAP[role as ChatRole] || role;
};
